import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Trash2, Edit, Plus, Save, Settings as SettingsIcon, Link, CheckCircle2, Unlink, X, UserCircle, Tag, Server, Globe, Shield, History, Bookmark, Mail, KeyRound, Send, RefreshCw, AlertCircle, Clock3, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const PRIORITY_COLORS = {
    critical: 'bg-red-100 text-red-700',
    high: 'bg-orange-100 text-orange-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-green-100 text-green-700',
};

const Settings = ({ user, onLogout }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('problem_types');

    // Problem Types
    const [problemTypes, setProblemTypes] = useState([]);
    const [editingPtId, setEditingPtId] = useState(null);
    const [editPtForm, setEditPtForm] = useState({ name: '', priority: '' });
    const [newPtForm, setNewPtForm] = useState({ name: '', priority: 'medium' });
    const [creatingPt, setCreatingPt] = useState(false);
    const [showAddPt, setShowAddPt] = useState(false);

    // Requesters
    const [requesters, setRequesters] = useState([]);
    const [showAddRequester, setShowAddRequester] = useState(false);
    const [newRequesterForm, setNewRequesterForm] = useState({ name: '', email: '' });
    const [creatingRequester, setCreatingRequester] = useState(false);
    const [editingRequesterId, setEditingRequesterId] = useState(null);
    const [editRequesterForm, setEditRequesterForm] = useState({ name: '', email: '' });
    const [savingRequester, setSavingRequester] = useState(false);

    // ClickUp connection status
    const [clickupConnected, setClickupConnected] = useState(false);
    const [clickupLoading, setClickupLoading] = useState(false);
    const [clickupDisconnecting, setClickupDisconnecting] = useState(false);

    // Infra Tags
    const [infraOptions, setInfraOptions] = useState({ hosting_locations: [], domain_registrars: [], dns_zone_editors: [] });
    const [newInfraTag, setNewInfraTag] = useState('');
    const [infraCategory, setInfraCategory] = useState('hosting_location');
    const [addingInfraTag, setAddingInfraTag] = useState(false);

    // Custom company tags
    const [customTags, setCustomTags] = useState([]);
    const [newCustomTag, setNewCustomTag] = useState('');
    const [addingCustomTag, setAddingCustomTag] = useState(false);

    // Audit logs
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [smtpForm, setSmtpForm] = useState({
        server_url: 'email-smtp.ca-central-1.amazonaws.com',
        server_port: 587,
        smtp_username: '',
        smtp_password: '',
        sender_email: 'noreply@webmarketersdev.ca',
        use_tls: true,
        password_configured: false
    });
    const [smtpSaving, setSmtpSaving] = useState(false);
    const [testEmail, setTestEmail] = useState(user?.email || '');
    const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
    const [notificationLogs, setNotificationLogs] = useState([]);
    const [notificationSummary, setNotificationSummary] = useState({ total: 0, sent: 0, failed: 0, pending: 0 });
    const [notificationLogsLoading, setNotificationLogsLoading] = useState(false);
    const [resendingNotification, setResendingNotification] = useState(null);

    const getAuthHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    const fetchData = async () => {
        try {
            const [ptRes, cuStatus, reqRes, infraRes, tagsRes, smtpRes] = await Promise.all([
                axios.get(`${API}/problem-types`, getAuthHeaders()),
                axios.get(`${API}/auth/clickup/status`, getAuthHeaders()).catch(() => ({ data: { connected: false } })),
                axios.get(`${API}/requesters`, getAuthHeaders()).catch(() => ({ data: [] })),
                axios.get(`${API}/companies/infra-options`, getAuthHeaders()).catch(() => ({ data: { hosting_locations: [], domain_registrars: [], dns_zone_editors: [] } })),
                axios.get(`${API}/companies/custom-tags`, getAuthHeaders()).catch(() => ({ data: [] })),
                axios.get(`${API}/admin/settings/smtp`, getAuthHeaders()).catch(() => ({ data: null })),
            ]);
            setProblemTypes(ptRes.data || []);
            setClickupConnected(cuStatus.data.connected);
            setRequesters(reqRes.data || []);
            setInfraOptions(infraRes.data || { hosting_locations: [], domain_registrars: [], dns_zone_editors: [] });
            setCustomTags(tagsRes.data || []);
            if (smtpRes.data) setSmtpForm(current => ({ ...current, ...smtpRes.data, smtp_password: '' }));
        } catch (error) {
            toast.error('Failed to load settings data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        const params = new URLSearchParams(window.location.search);
        if (params.get('clickup_connected') === '1') {
            toast.success('ClickUp connected! Tasks and comments will now show your profile.');
            setClickupConnected(true);
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []); // eslint-disable-line

    // ClickUp handlers
    const handleConnectClickup = async () => {
        setClickupLoading(true);
        try {
            const res = await axios.get(`${API}/auth/clickup/connect`, getAuthHeaders());
            window.location.href = res.data.auth_url;
        } catch (err) {
            toast.error('Failed to start ClickUp connection');
            setClickupLoading(false);
        }
    };

    const handleDisconnectClickup = async () => {
        if (!window.confirm('Disconnect your ClickUp account?')) return;
        setClickupDisconnecting(true);
        try {
            await axios.delete(`${API}/auth/clickup/disconnect`, getAuthHeaders());
            setClickupConnected(false);
            toast.success('ClickUp disconnected.');
        } catch (err) {
            toast.error('Failed to disconnect ClickUp');
        } finally {
            setClickupDisconnecting(false);
        }
    };

    // Problem Type handlers
    const handleCreateProblemType = async (e) => {
        e.preventDefault();
        if (!newPtForm.name.trim()) return;
        setCreatingPt(true);
        try {
            const res = await axios.post(`${API}/problem-types`, newPtForm, getAuthHeaders());
            setProblemTypes(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
            setNewPtForm({ name: '', priority: 'medium' });
            setShowAddPt(false);
            toast.success('Problem type created');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create problem type');
        } finally {
            setCreatingPt(false);
        }
    };

    const handleUpdateProblemType = async (e, id) => {
        e.preventDefault();
        if (!editPtForm.name.trim()) return;
        try {
            const res = await axios.put(`${API}/admin/problem-types/${id}`, editPtForm, getAuthHeaders());
            setProblemTypes(prev => prev.map(p => p.id === id ? res.data : p));
            setEditingPtId(null);
            toast.success('Updated');
        } catch (error) {
            toast.error('Failed to update');
        }
    };

    const handleDeleteProblemType = async (id) => {
        if (!window.confirm('Delete this problem type?')) return;
        try {
            await axios.delete(`${API}/admin/problem-types/${id}`, getAuthHeaders());
            setProblemTypes(prev => prev.filter(p => p.id !== id));
            toast.success('Deleted');
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    // Requester handlers
    const handleCreateRequester = async (e) => {
        e.preventDefault();
        if (!newRequesterForm.name.trim()) return;
        setCreatingRequester(true);
        try {
            const res = await axios.post(`${API}/requesters`, {
                name: newRequesterForm.name.trim(),
                email: newRequesterForm.email.trim() || null
            }, getAuthHeaders());
            setRequesters(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
            setNewRequesterForm({ name: '', email: '' });
            setShowAddRequester(false);
            toast.success('Requester created');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create requester');
        } finally {
            setCreatingRequester(false);
        }
    };

    const startEditRequester = requester => {
        setEditingRequesterId(requester.id);
        setEditRequesterForm({ name: requester.name || '', email: requester.email || '' });
        setShowAddRequester(false);
    };

    const handleUpdateRequester = async event => {
        event.preventDefault();
        if (!editRequesterForm.name.trim()) return toast.error('Requester name is required');
        setSavingRequester(true);
        try {
            const response = await axios.patch(
                `${API}/requesters/${editingRequesterId}`,
                {
                    name: editRequesterForm.name.trim(),
                    email: editRequesterForm.email.trim() || null
                },
                getAuthHeaders()
            );
            setRequesters(previous => previous
                .map(requester => requester.id === editingRequesterId ? response.data : requester)
                .sort((a, b) => a.name.localeCompare(b.name)));
            setEditingRequesterId(null);
            toast.success('Requester updated');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update requester');
        } finally {
            setSavingRequester(false);
        }
    };

    const handleDeleteRequester = async (id) => {
        if (!window.confirm('Delete this requester?')) return;
        try {
            await axios.delete(`${API}/requesters/${id}`, getAuthHeaders());
            setRequesters(prev => prev.filter(r => r.id !== id));
            toast.success('Requester deleted');
        } catch (error) {
            toast.error('Failed to delete requester');
        }
    };

    // Infra tag handlers
    const handleAddInfraTag = async () => {
        if (!newInfraTag.trim()) return;
        setAddingInfraTag(true);
        try {
            // Create a temp company entry to seed the tag, then delete it.
            // Instead, we use the PATCH info endpoint on an existing company.
            // Simpler: add directly to DB via a new endpoint
            await axios.post(`${API}/companies/infra-tags`, {
                field: infraCategory,
                value: newInfraTag.trim()
            }, getAuthHeaders());
            setNewInfraTag('');
            toast.success('Tag added');
            const infraRes = await axios.get(`${API}/companies/infra-options`, getAuthHeaders());
            setInfraOptions(infraRes.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add tag');
        } finally {
            setAddingInfraTag(false);
        }
    };

    const handleDeleteInfraTag = async (field, value) => {
        if (!window.confirm(`Remove "${value}" from all companies?`)) return;
        try {
            await axios.post(`${API}/companies/infra-tags/delete`, { field, value }, getAuthHeaders());
            toast.success('Tag removed from all companies');
            const infraRes = await axios.get(`${API}/companies/infra-options`, getAuthHeaders());
            setInfraOptions(infraRes.data);
        } catch { toast.error('Failed to remove tag'); }
    };

    // Custom company tag handlers
    const handleAddCustomTag = async () => {
        if (!newCustomTag.trim()) return;
        setAddingCustomTag(true);
        try {
            await axios.post(`${API}/companies/custom-tags`, { value: newCustomTag.trim() }, getAuthHeaders());
            setNewCustomTag('');
            toast.success('Tag created');
            const res = await axios.get(`${API}/companies/custom-tags`, getAuthHeaders());
            setCustomTags(res.data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create tag');
        } finally {
            setAddingCustomTag(false);
        }
    };

    const handleDeleteCustomTag = async (tag) => {
        if (!window.confirm(`Delete "${tag}" tag and remove from all companies?`)) return;
        try {
            await axios.delete(`${API}/companies/custom-tags/${encodeURIComponent(tag)}`, getAuthHeaders());
            toast.success('Tag deleted');
            setCustomTags(prev => prev.filter(t => t !== tag));
        } catch { toast.error('Failed to delete tag'); }
    };

    // Audit log fetch
    const fetchAuditLogs = async () => {
        setAuditLoading(true);
        try {
            const res = await axios.get(`${API}/audit-logs?limit=100`, getAuthHeaders());
            setAuditLogs(res.data || []);
        } catch { toast.error('Failed to load audit logs'); }
        finally { setAuditLoading(false); }
    };

    const fetchNotificationLogs = async () => {
        setNotificationLogsLoading(true);
        try {
            const response = await axios.get(`${API}/admin/settings/notification-logs?limit=200`, getAuthHeaders());
            setNotificationLogs(response.data.items || []);
            setNotificationSummary(response.data.summary || { total: 0, sent: 0, failed: 0, pending: 0 });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to load email notification logs');
        } finally {
            setNotificationLogsLoading(false);
        }
    };

    const resendNotificationEmail = async eventKey => {
        setResendingNotification(eventKey);
        try {
            const response = await axios.post(
                `${API}/admin/settings/notification-logs/${encodeURIComponent(eventKey)}/resend`,
                {},
                getAuthHeaders()
            );
            toast.success(response.data.message);
            await fetchNotificationLogs();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to resend email');
            await fetchNotificationLogs();
        } finally {
            setResendingNotification(null);
        }
    };

    const openNotificationTrackingPage = async eventKey => {
        const trackingWindow = window.open('', '_blank');
        try {
            const response = await axios.get(
                `${API}/admin/settings/notification-logs/${encodeURIComponent(eventKey)}/tracking-url`,
                getAuthHeaders()
            );
            if (trackingWindow) trackingWindow.location.href = response.data.tracking_url;
            else window.open(response.data.tracking_url, '_blank', 'noopener,noreferrer');
        } catch (error) {
            if (trackingWindow) trackingWindow.close();
            toast.error(error.response?.data?.detail || 'Failed to open tracking page');
        }
    };

    const saveSmtp = async event => {
        event.preventDefault();
        setSmtpSaving(true);
        try {
            await axios.put(`${API}/admin/settings/smtp`, {
                ...smtpForm,
                server_port: Number(smtpForm.server_port),
                smtp_password: smtpForm.smtp_password || null
            }, getAuthHeaders());
            setSmtpForm(current => ({ ...current, smtp_password: '', password_configured: true }));
            toast.success('SMTP settings saved');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save SMTP settings');
        } finally {
            setSmtpSaving(false);
        }
    };

    const sendTestEmail = async () => {
        try {
            const response = await axios.post(`${API}/admin/settings/smtp/test`, { recipient_email: testEmail }, getAuthHeaders());
            toast.success(response.data.message);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'SMTP test failed');
        }
    };

    const changePassword = async event => {
        event.preventDefault();
        if (passwordForm.new_password !== passwordForm.confirm_password) return toast.error('Passwords do not match');
        try {
            await axios.post(`${API}/auth/change-password`, {
                current_password: passwordForm.current_password,
                new_password: passwordForm.new_password
            }, getAuthHeaders());
            setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
            toast.success('Password changed successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    const infraTotal = (infraOptions.hosting_locations?.length || 0) + (infraOptions.domain_registrars?.length || 0) + (infraOptions.dns_zone_editors?.length || 0);
    const tabs = [
        { id: 'problem_types', label: 'Problem Types', icon: Tag, count: problemTypes.length },
        { id: 'requesters', label: 'Requesters', icon: UserCircle, count: requesters.length },
        { id: 'infra_tags', label: 'Infra Tags', icon: Server, count: infraTotal },
        { id: 'custom_tags', label: 'Company Tags', icon: Bookmark, count: customTags.length },
        { id: 'smtp', label: 'Email / SMTP', icon: Mail },
        { id: 'email_logs', label: 'Email Log', icon: Send, count: notificationSummary.total },
        { id: 'security', label: 'My Password', icon: KeyRound },
        { id: 'audit_log', label: 'Activity Log', icon: History },
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Compact Header */}
            <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 flex items-center gap-3">
                <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                </button>
                <SettingsIcon className="w-4 h-4 text-indigo-600" />
                <h1 className="text-base font-semibold text-gray-900">System Settings</h1>
            </div>

            <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">

                {/* ClickUp Integration — compact card */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                            {clickupConnected
                                ? <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
                                : <Unlink className="w-5 h-5 text-gray-300 shrink-0" />
                            }
                            <div>
                                <p className="text-sm font-medium text-gray-800">
                                    ClickUp Integration
                                    {clickupConnected && <span className="ml-2 text-xs font-normal text-green-600">Connected</span>}
                                </p>
                                <p className="text-xs text-gray-400">
                                    {clickupConnected
                                        ? 'Tasks and comments will be attributed to your ClickUp profile.'
                                        : 'Connect your account so tasks/comments appear as you in ClickUp.'}
                                </p>
                            </div>
                        </div>
                        {clickupConnected ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleDisconnectClickup}
                                disabled={clickupDisconnecting}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 text-xs h-7 px-2"
                            >
                                {clickupDisconnecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3 mr-1" />}
                                Disconnect
                            </Button>
                        ) : (
                            <Button
                                size="sm"
                                onClick={handleConnectClickup}
                                disabled={clickupLoading}
                                className="bg-violet-600 hover:bg-violet-700 text-white text-xs h-7 px-3"
                            >
                                {clickupLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Link className="w-3 h-3 mr-1" />}
                                Connect ClickUp
                            </Button>
                        )}
                    </div>
                </div>

                {/* Settings navigation and content */}
                <div className="lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:items-start gap-4">
                    <nav className="bg-white rounded-lg border border-gray-200 overflow-hidden lg:sticky lg:top-4">
                        <div className="px-4 pt-3 pb-2 hidden lg:block">
                            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">Settings Menu</p>
                        </div>
                        <div className="flex lg:flex-col overflow-x-auto lg:overflow-visible border-b lg:border-b-0 border-gray-100 p-1.5 lg:p-2">
                        {tabs.map(tab => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => {
                                        setActiveTab(tab.id);
                                        if (tab.id === 'audit_log') fetchAuditLogs();
                                        if (tab.id === 'email_logs') fetchNotificationLogs();
                                    }}
                                    className={`flex items-center gap-2 px-3 py-2.5 text-xs font-medium whitespace-nowrap rounded-md transition-colors lg:w-full ${
                                        isActive
                                            ? 'text-indigo-700 bg-indigo-50'
                                            : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
                                >
                                    <Icon className="w-4 h-4 shrink-0" />
                                    <span className="lg:flex-1 lg:text-left">{tab.label}</span>
                                    {typeof tab.count === 'number' && (
                                        <span className={`text-[10px] font-bold min-w-5 text-center px-1.5 py-0.5 rounded-full ${isActive ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'}`}>
                                            {tab.count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                        </div>
                    </nav>

                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mt-4 lg:mt-0 min-w-0">

                    {/* Problem Types Tab */}
                    {activeTab === 'problem_types' && (
                        <div>
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                                <p className="text-xs text-gray-400">{problemTypes.length} types configured</p>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setShowAddPt(v => !v); setNewPtForm({ name: '', priority: 'medium' }); }}
                                    className="h-7 px-2 text-xs gap-1"
                                >
                                    {showAddPt ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                                    {showAddPt ? 'Cancel' : 'Add'}
                                </Button>
                            </div>

                            {showAddPt && (
                                <form onSubmit={handleCreateProblemType} className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border-b border-indigo-100">
                                    <Input placeholder="Type name…" value={newPtForm.name} onChange={e => setNewPtForm({ ...newPtForm, name: e.target.value })} required className="h-7 text-xs flex-1" autoFocus />
                                    <Select value={newPtForm.priority} onValueChange={v => setNewPtForm({ ...newPtForm, priority: v })}>
                                        <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="low">Low</SelectItem>
                                            <SelectItem value="medium">Medium</SelectItem>
                                            <SelectItem value="high">High</SelectItem>
                                            <SelectItem value="critical">Critical</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Button type="submit" size="sm" disabled={creatingPt} className="h-7 px-2 text-xs bg-indigo-600 hover:bg-indigo-700">
                                        {creatingPt ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                    </Button>
                                </form>
                            )}

                            <div className="divide-y divide-gray-50 max-h-[50vh] overflow-y-auto">
                                {problemTypes.length === 0 ? (
                                    <p className="text-xs text-gray-400 italic px-4 py-3">No problem types yet.</p>
                                ) : problemTypes.map(pt => (
                                    <div key={pt.id} className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 transition-colors group">
                                        {editingPtId === pt.id ? (
                                            <form onSubmit={(e) => handleUpdateProblemType(e, pt.id)} className="flex items-center gap-2 w-full">
                                                <Input value={editPtForm.name} onChange={e => setEditPtForm({ ...editPtForm, name: e.target.value })} required className="h-6 text-xs flex-1" autoFocus />
                                                <Select value={editPtForm.priority} onValueChange={v => setEditPtForm({ ...editPtForm, priority: v })}>
                                                    <SelectTrigger className="h-6 text-xs w-24"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="low">Low</SelectItem>
                                                        <SelectItem value="medium">Medium</SelectItem>
                                                        <SelectItem value="high">High</SelectItem>
                                                        <SelectItem value="critical">Critical</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <button type="submit" className="text-green-600 hover:text-green-700 p-0.5"><Save className="w-3.5 h-3.5" /></button>
                                                <button type="button" onClick={() => setEditingPtId(null)} className="text-gray-400 hover:text-gray-600 p-0.5"><X className="w-3.5 h-3.5" /></button>
                                            </form>
                                        ) : (
                                            <>
                                                <span className="text-sm text-gray-700 flex-1">{pt.name}</span>
                                                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${PRIORITY_COLORS[pt.priority] || 'bg-gray-100 text-gray-600'}`}>
                                                    {pt.priority}
                                                </span>
                                                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={() => { setEditingPtId(pt.id); setEditPtForm({ name: pt.name, priority: pt.priority }); }} className="p-1 text-gray-400 hover:text-blue-600 rounded">
                                                        <Edit className="w-3 h-3" />
                                                    </button>
                                                    <button onClick={() => handleDeleteProblemType(pt.id)} className="p-1 text-gray-400 hover:text-red-600 rounded">
                                                        <Trash2 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Requesters Tab */}
                    {activeTab === 'requesters' && (
                        <div>
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                                <p className="text-xs text-gray-400">{requesters.length} requesters</p>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        setShowAddRequester(v => !v);
                                        setNewRequesterForm({ name: '', email: '' });
                                        setEditingRequesterId(null);
                                    }}
                                    className="h-7 px-2 text-xs gap-1"
                                >
                                    {showAddRequester ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                                    {showAddRequester ? 'Cancel' : 'Add'}
                                </Button>
                            </div>

                            {showAddRequester && (
                                <form onSubmit={handleCreateRequester} className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border-b border-indigo-100">
                                    <Input placeholder="Full name…" value={newRequesterForm.name} onChange={e => setNewRequesterForm({ ...newRequesterForm, name: e.target.value })} required className="h-7 text-xs flex-1" autoFocus />
                                    <Input placeholder="Email (optional)" value={newRequesterForm.email} onChange={e => setNewRequesterForm({ ...newRequesterForm, email: e.target.value })} className="h-7 text-xs flex-1" />
                                    <Button type="submit" size="sm" disabled={creatingRequester} className="h-7 px-2 text-xs bg-indigo-600 hover:bg-indigo-700">
                                        {creatingRequester ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                    </Button>
                                </form>
                            )}

                            <div className="divide-y divide-gray-50 max-h-[50vh] overflow-y-auto">
                                {requesters.length === 0 ? (
                                    <p className="text-xs text-gray-400 italic px-4 py-3">No requesters yet.</p>
                                ) : requesters.map(r => editingRequesterId === r.id ? (
                                    <form key={r.id} onSubmit={handleUpdateRequester} className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3 bg-blue-50 border-y border-blue-100">
                                        <Input
                                            value={editRequesterForm.name}
                                            onChange={event => setEditRequesterForm({ ...editRequesterForm, name: event.target.value })}
                                            placeholder="Full name"
                                            required
                                            autoFocus
                                            className="h-8 text-xs flex-1 bg-white"
                                        />
                                        <Input
                                            type="email"
                                            value={editRequesterForm.email}
                                            onChange={event => setEditRequesterForm({ ...editRequesterForm, email: event.target.value })}
                                            placeholder="Email address"
                                            className="h-8 text-xs flex-1 bg-white"
                                        />
                                        <div className="flex items-center gap-1">
                                            <Button type="submit" size="sm" disabled={savingRequester} className="h-8 px-2 text-xs gap-1">
                                                {savingRequester ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}Save
                                            </Button>
                                            <Button type="button" size="sm" variant="ghost" onClick={() => setEditingRequesterId(null)} className="h-8 px-2 text-xs">Cancel</Button>
                                        </div>
                                    </form>
                                ) : (
                                    <div key={r.id} className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 transition-colors group">
                                        <UserCircle className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                                        <span className="text-sm text-gray-700 flex-1 truncate">{r.name}</span>
                                        {r.email
                                            ? <span className="text-xs text-gray-400 truncate max-w-[220px]">{r.email}</span>
                                            : <span className="text-xs text-amber-600">No email</span>}
                                        <button onClick={() => startEditRequester(r)} className="p-1 text-gray-400 hover:text-blue-600 rounded opacity-0 group-hover:opacity-100 transition-opacity" title="Edit requester">
                                            <Edit className="w-3 h-3" />
                                        </button>
                                        <button onClick={() => handleDeleteRequester(r.id)} className="p-1 text-gray-400 hover:text-red-600 rounded opacity-0 group-hover:opacity-100 transition-opacity" title="Delete requester">
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Infrastructure Tags Tab */}
                    {activeTab === 'infra_tags' && (
                        <div>
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                                <p className="text-xs text-gray-400">{infraTotal} tags across 3 categories</p>
                            </div>

                            {/* Add new tag */}
                            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
                                <Select value={infraCategory} onValueChange={setInfraCategory}>
                                    <SelectTrigger className="h-7 text-xs w-40">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="hosting_location">Hosting Location</SelectItem>
                                        <SelectItem value="domain_registrar">Domain Registrar</SelectItem>
                                        <SelectItem value="dns_zone_editor">DNS Zone Editor</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Input
                                    placeholder="New tag name..."
                                    value={newInfraTag}
                                    onChange={e => setNewInfraTag(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleAddInfraTag()}
                                    className="h-7 text-xs flex-1"
                                />
                                <Button size="sm" onClick={handleAddInfraTag} disabled={addingInfraTag || !newInfraTag.trim()} className="h-7 px-2 text-xs bg-indigo-600 hover:bg-indigo-700">
                                    {addingInfraTag ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                                </Button>
                            </div>

                            {/* Hosting Locations */}
                            {[['Hosting Locations', infraOptions.hosting_locations || [], Server, 'hosting_location', 'bg-emerald-100 text-emerald-700'],
                              ['Domain Registrars', infraOptions.domain_registrars || [], Globe, 'domain_registrar', 'bg-blue-100 text-blue-700'],
                              ['DNS Zone Editors', infraOptions.dns_zone_editors || [], Shield, 'dns_zone_editor', 'bg-violet-100 text-violet-700'],
                            ].map(([title, tags, Icon, field, colorCls]) => (
                                <div key={field}>
                                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-50/50 border-b border-gray-100">
                                        <Icon className="w-3.5 h-3.5 text-gray-400" />
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{title}</span>
                                        <span className="text-[10px] text-gray-400">({tags.length})</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 px-4 py-2.5 min-h-[36px]">
                                        {tags.length === 0 ? (
                                            <span className="text-xs text-gray-400 italic">No tags yet</span>
                                        ) : tags.map(tag => (
                                            <span key={tag} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${colorCls} group`}>
                                                {tag}
                                                <button onClick={() => handleDeleteInfraTag(field, tag)} className="opacity-0 group-hover:opacity-100 transition-opacity text-current hover:text-red-600 ml-0.5">
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Custom Company Tags Tab */}
                    {activeTab === 'custom_tags' && (
                        <div>
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                                <p className="text-xs text-gray-400">{customTags.length} custom tags</p>
                            </div>
                            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
                                <Input
                                    placeholder="New tag (e.g. VIP, Monthly Retainer)..."
                                    value={newCustomTag}
                                    onChange={e => setNewCustomTag(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleAddCustomTag()}
                                    className="h-7 text-xs flex-1"
                                />
                                <Button size="sm" onClick={handleAddCustomTag} disabled={addingCustomTag || !newCustomTag.trim()} className="h-7 px-2 text-xs bg-indigo-600 hover:bg-indigo-700">
                                    {addingCustomTag ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                                </Button>
                            </div>
                            <div className="flex flex-wrap gap-2 px-4 py-3 min-h-[60px]">
                                {customTags.length === 0 ? (
                                    <span className="text-xs text-gray-400 italic">No custom tags yet. Create tags like "VIP", "Monthly Retainer", "Needs Follow-Up"</span>
                                ) : customTags.map(tag => (
                                    <span key={tag} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 group">
                                        <Bookmark className="w-3 h-3" />
                                        {tag}
                                        <button onClick={() => handleDeleteCustomTag(tag)} className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-600 ml-0.5">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'smtp' && (
                        <div className="p-4 space-y-5">
                            <div>
                                <h3 className="font-semibold text-gray-900">AWS SES SMTP</h3>
                                <p className="text-xs text-gray-500 mt-1">Credentials are encrypted. The saved password is never displayed.</p>
                            </div>
                            <form onSubmit={saveSmtp} className="grid sm:grid-cols-2 gap-4">
                                <div className="sm:col-span-2"><Label>Server URL</Label><Input value={smtpForm.server_url} onChange={e => setSmtpForm({ ...smtpForm, server_url: e.target.value })} required /></div>
                                <div><Label>Server Port</Label><Input type="number" value={smtpForm.server_port} onChange={e => setSmtpForm({ ...smtpForm, server_port: e.target.value })} required /></div>
                                <div><Label>Sender Email</Label><Input type="email" value={smtpForm.sender_email} onChange={e => setSmtpForm({ ...smtpForm, sender_email: e.target.value })} required /></div>
                                <div className="sm:col-span-2"><Label>SMTP Username</Label><Input value={smtpForm.smtp_username} onChange={e => setSmtpForm({ ...smtpForm, smtp_username: e.target.value })} required /></div>
                                <div className="sm:col-span-2"><Label>SMTP Password</Label><Input type="password" value={smtpForm.smtp_password} onChange={e => setSmtpForm({ ...smtpForm, smtp_password: e.target.value })} placeholder={smtpForm.password_configured ? 'Saved securely; enter only to replace' : 'Enter SES SMTP password'} /></div>
                                <p className="sm:col-span-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Use credentials generated from AWS SES → SMTP settings in Canada Central. IAM access keys do not work as SES SMTP credentials.</p>
                                <label className="sm:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={smtpForm.use_tls} onChange={e => setSmtpForm({ ...smtpForm, use_tls: e.target.checked })} />Use STARTTLS</label>
                                <div className="sm:col-span-2"><Button disabled={smtpSaving}>{smtpSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save SMTP Settings</Button></div>
                            </form>
                            <div className="border-t pt-4">
                                <Label>Send Test Email</Label>
                                <div className="flex gap-2 mt-1"><Input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} /><Button type="button" variant="outline" onClick={sendTestEmail}><Send className="w-4 h-4 mr-2" />Test</Button></div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'email_logs' && (
                        <div>
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
                                <div>
                                    <p className="text-sm font-semibold text-gray-800">Client Notification Emails</p>
                                    <p className="text-xs text-gray-400 mt-0.5">Delivery history for task-created and status-change emails.</p>
                                </div>
                                <Button size="sm" variant="outline" onClick={fetchNotificationLogs} disabled={notificationLogsLoading} className="h-8 px-3 text-xs gap-1.5">
                                    {notificationLogsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                    Refresh
                                </Button>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-100 border-b border-gray-100">
                                {[
                                    ['Total', notificationSummary.total, Mail, 'text-gray-600'],
                                    ['Sent', notificationSummary.sent, CheckCircle2, 'text-green-600'],
                                    ['Failed', notificationSummary.failed, AlertCircle, 'text-red-600'],
                                    ['Pending', notificationSummary.pending, Clock3, 'text-amber-600'],
                                ].map(([label, value, Icon, color]) => (
                                    <div key={label} className="bg-white px-4 py-3">
                                        <div className="flex items-center gap-1.5">
                                            <Icon className={`w-3.5 h-3.5 ${color}`} />
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</span>
                                        </div>
                                        <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
                                    </div>
                                ))}
                            </div>

                            {notificationLogsLoading ? (
                                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                            ) : notificationLogs.length === 0 ? (
                                <div className="text-center py-12">
                                    <Mail className="w-9 h-9 text-gray-200 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500">No client notification emails recorded yet</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100 max-h-[58vh] overflow-y-auto">
                                    {notificationLogs.map(item => (
                                        <div key={item.event_key} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                                            <div className="flex flex-wrap items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                                                            item.status === 'sent'
                                                                ? 'bg-green-100 text-green-700'
                                                                : item.status === 'failed'
                                                                    ? 'bg-red-100 text-red-700'
                                                                    : 'bg-amber-100 text-amber-700'
                                                        }`}>{item.status}</span>
                                                        <span className="text-xs font-semibold text-indigo-700">{item.event_label}</span>
                                                    </div>
                                                    <p className="text-sm font-medium text-gray-800 mt-1 truncate">{item.ticket_title}</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                        {item.requester_name} · {item.recipient}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                                        {new Date(item.sent_at || item.created_at).toLocaleString()}
                                                    </span>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => openNotificationTrackingPage(item.event_key)}
                                                        className="h-7 px-2 text-xs gap-1"
                                                    >
                                                        <ExternalLink className="w-3 h-3" />
                                                        View link
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => resendNotificationEmail(item.event_key)}
                                                        disabled={resendingNotification === item.event_key}
                                                        className="h-7 px-2 text-xs gap-1"
                                                    >
                                                        {resendingNotification === item.event_key
                                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                                            : <RefreshCw className="w-3 h-3" />}
                                                        Resend
                                                    </Button>
                                                </div>
                                            </div>
                                            {item.resend_count > 0 && (
                                                <p className="text-[10px] text-gray-400 mt-1">Resent {item.resend_count} time{item.resend_count === 1 ? '' : 's'}</p>
                                            )}
                                            {item.error && (
                                                <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2.5 py-2 mt-2 break-words">{item.error}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'security' && (
                        <form onSubmit={changePassword} className="p-4 space-y-4">
                            <div><h3 className="font-semibold">Change My Password</h3><p className="text-xs text-gray-500 mt-1">Your current password is required.</p></div>
                            <div><Label>Current Password</Label><Input type="password" value={passwordForm.current_password} onChange={e => setPasswordForm({ ...passwordForm, current_password: e.target.value })} required /></div>
                            <div><Label>New Password</Label><Input type="password" value={passwordForm.new_password} onChange={e => setPasswordForm({ ...passwordForm, new_password: e.target.value })} required /></div>
                            <div><Label>Confirm New Password</Label><Input type="password" value={passwordForm.confirm_password} onChange={e => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} required /></div>
                            <Button><KeyRound className="w-4 h-4 mr-2" />Change Password</Button>
                        </form>
                    )}

                    {/* Activity Log Tab */}
                    {activeTab === 'audit_log' && (
                        <div>
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                                <p className="text-xs text-gray-400">{auditLogs.length} recent activities</p>
                                <Button size="sm" variant="outline" onClick={fetchAuditLogs} className="h-7 px-2 text-xs gap-1">
                                    <History className="w-3 h-3" /> Refresh
                                </Button>
                            </div>
                            {auditLoading ? (
                                <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                            ) : auditLogs.length === 0 ? (
                                <div className="text-center py-8">
                                    <History className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                                    <p className="text-xs text-gray-400">No activity recorded yet</p>
                                    <p className="text-[10px] text-gray-300 mt-1">Changes to company info, credentials, and knowledge notes will appear here</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-50 max-h-[50vh] overflow-y-auto">
                                    {auditLogs.map(log => (
                                        <div key={log.id} className="px-4 py-2.5 hover:bg-gray-50 transition-colors">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-medium text-gray-700">{log.user_email}</span>
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                                    log.action === 'update_info' ? 'bg-blue-100 text-blue-700' :
                                                    log.action === 'add_note' ? 'bg-green-100 text-green-700' :
                                                    log.action === 'delete_note' ? 'bg-red-100 text-red-700' :
                                                    'bg-gray-100 text-gray-600'
                                                }`}>{log.action.replace(/_/g, ' ')}</span>
                                                <span className="text-[10px] text-gray-400 ml-auto">{new Date(log.created_at).toLocaleString()}</span>
                                            </div>
                                            <p className="text-[11px] text-gray-500 mt-0.5">
                                                <span className="font-medium text-gray-600">{log.entity_name}</span> — {log.details}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Settings;
